import { config } from '@/config';
import { defaultDaySchedules } from '@/constants';
import { SHOP_CUSTOM_RULE_MESSAGE_TEXT } from '@/constants/custom-shop-name';
import { CartDateDisplayType, DisplayModeEnum } from '@/constants/enum';
import {
  defaultSoldOutText,
  defaultVariablesColor,
  END_DATE_MARKER,
  START_DATE_MARKER,
  TIME_COUNTDOWN_MARKER,
} from '@/constants/string';
import { IDefaultOpenModalAddCondition } from '@/pages/Settings/Components/EstimateRules/components/CURule/components/DisplayConditions';
import { IRule, RelationCondition } from '@/types/newSetup.type';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

export type IRuleErrorField =
  | 'product'
  | 'exceptionProduct'
  | 'collection'
  | 'vendor'
  | 'cartLabelText'
  | 'cartCustomText'
  | 'customMessageSoldOut'
  | 'zipcode'
  | 'country'
  | 'state'
  | 'ruleName'
  | 'messageText'
  | 'serverError'
  | 'tooltipText'
  | 'productTags'
  | 'productMetafield'
  | 'variantMetafield'
  | 'inventoryLocations'
  | 'skus'
  | 'inventoryQuantity'
  | 'preOrderDate'
  | 'productMetafieldItem'
  | 'variantMetafieldItem'
  | 'daySchedules'
  | 'dateSchedules';
export interface IRuleError {
  field: IRuleErrorField;
  msg: string;
}

// Define the initial state using that type
export const initialRuleState: IRule = {
  maxDeliveryDays: 3,
  minDeliveryDays: 0,
  maxPrepDays: 0,
  minPrepDays: 0,
  enable: true,
  estimatedText: SHOP_CUSTOM_RULE_MESSAGE_TEXT.includes(config.shop || '')
    ? `<div style="text-align: center">Estimated Delivery Time: <b>${START_DATE_MARKER}</b> to <b>${END_DATE_MARKER}</b></div>`
    : `⏰ Order today within <strong style="color: ${defaultVariablesColor};">${TIME_COUNTDOWN_MARKER}</strong> to get discounted price<br>🚚 Delivery from <strong style="color: ${defaultVariablesColor};">${START_DATE_MARKER}</strong> to <strong style="color: ${defaultVariablesColor};">${END_DATE_MARKER}</strong>`,
  showTextInCart: false,
  cartPageLabelText: 'Estimated between',
  cartPageDateDisplay: `${START_DATE_MARKER} and ${END_DATE_MARKER}`,
  cartPageDateDisplayType: CartDateDisplayType.MessageText,
  isShowDeliveryMethodName: true,
  name: 'Name of rule',
  styledName: 'Name of rule',
  itemConditions: [],
  locationConditions: [],
  privacyPageUrl: '',
  privacyPageText: '',
  privacyMore: '',
  soldOutCustomTextEnabled: false,
  soldOutCustomText: defaultSoldOutText,
  soldOutEnabled: false,
  typeRelationOfItemCondition: RelationCondition.OR,
  preOrderDate: null,
  isUsePreOrderDate: false,
  displayMode: DisplayModeEnum.AllTime,
  daySchedules: defaultDaySchedules,
};

const initError: IRuleError[] = [];

export const ruleSlice = createSlice({
  name: 'rule',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: {
    state: initialRuleState,
    isDefaultOpenModalAddCondition: {
      product: false,
      collection: false,
      vendor: false,
      inventoryLocations: false,
      productTags: false,
      skus: false,
      exception: false,
    },
    errors: initError,
  },
  reducers: {
    handleChange: (
      state,
      action: PayloadAction<{
        key: keyof IRule;
        value: IRule[keyof IRule];
      }>,
    ) => {
      state.state = {
        ...state.state,
        [action.payload.key]: action.payload.value,
      };

      const serverError = state.errors.find((error) => error.field === 'serverError');
      if (serverError) {
        state.errors = state.errors.filter((error) => error.field !== 'serverError');
      }
    },
    setValues: (
      state,
      action: PayloadAction<{
        value: IRule;
      }>,
    ) => {
      state.state = action.payload.value;
    },
    handleInit: (state) => {
      state.state = initialRuleState;
      state.errors = initError;
    },
    handleSetError: (state, action: PayloadAction<{ value: IRuleError }>) => {
      if (action.payload.value.msg) {
        const index = state.errors.findIndex((item) => item.field === action.payload.value.field);
        if (index !== -1) {
          state.errors = state.errors.map((error, i) => (i === index ? action.payload.value : error));
        } else {
          state.errors = [...state.errors, action.payload.value];
        }
      } else {
        state.errors = state.errors.filter((item) => item.field !== action.payload.value.field);
      }
    },
    clearError: (state) => {
      state.errors = [];
    },
    handleSetIsDefaultOpenModalAddCondition: (state, action: PayloadAction<IDefaultOpenModalAddCondition>) => {
      state.isDefaultOpenModalAddCondition = action.payload;
    },
    handleResetIsDefaultOpenModalAddCondition: (state) => {
      state.isDefaultOpenModalAddCondition = {
        product: false,
        collection: false,
        vendor: false,
        inventoryLocations: false,
        productTags: false,
        skus: false,
        exception: false,
      };
    },
  },
});

// Other code such as selectors can use the imported `RootState` type

export const ruleSettingsSelector = createSelector(
  (state: RootState) => state.rule.state,
  (state) => state,
);
export const initRuleSettingsSelector = createSelector(
  () => initialRuleState,
  (state) => state,
);

export const ruleErrorsSelector = createSelector(
  (state: RootState) => state.rule.errors,
  (state) => state,
);

export const isDefaultOpenModalAddConditionSelector = createSelector(
  (state: RootState) => state.rule.isDefaultOpenModalAddCondition,
  (state) => state,
);

export default ruleSlice;
